import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { SelectField, TextAreaField, UploadField } from '@/components/forms';
import { UPLOAD_PRESETS, type UploadFn } from '@/lib/upload';

import { WARRANTY_COPY, WARRANTY_DESCRIPTION_MAX, WARRANTY_REASONS } from '../constants';
import { useRaiseWarrantyClaim } from '../hooks';
import {
  warrantyClaimFormSchema,
  type DeviceOption,
  type WarrantyClaimFormValues,
} from '../schemas';

import styles from './warranty-claim-form-drawer.module.scss';

export type WarrantyClaimFormDrawerProps = {
  open: boolean;
  /** Cancel / x / Esc — returns the Grower wherever they came from. No claim is created. */
  onClose: () => void;
  /** The claim was raised. Separate from `onClose` so the caller can distinguish the outcomes. */
  onRaised: () => void;
  /**
   * The Devices this claim may be raised against.
   *
   * One entry in the Device flow — held by the page already, so the form needs no fetch of its own.
   * The server's eligible list account-wide. Nothing here filters it: a Device that cannot be
   * claimed against is simply absent (ADR-0019).
   */
  devices: DeviceOption[];
  /**
   * Preselects and **locks** the Device field (113581 §A "disabled (locked)"). Supplying it *is*
   * what makes the Device flow the Device flow; the account-wide flow omits it and the field is
   * open to selection (Figma `430:403693`).
   */
  defaultCustomerDeviceId?: string;
  /** Seam 2 (spec.md § Testing decisions): the transport is injected, never reached for. */
  onUpload: UploadFn;
};

const REASON_OPTIONS = Object.entries(WARRANTY_REASONS).map(([value, label]) => ({ value, label }));

/**
 * The Claim Warranty intake form (113319; Figma `944:563818` locked / `430:403693` open).
 *
 * One Device per claim, always. `defaultCustomerDeviceId` decides which flow this is: set, the
 * Grower arrived from a Device and the field is preselected and locked; absent, they arrived from
 * their Profile and choose from the Devices the server says are eligible (ADR-0019).
 */
export function WarrantyClaimFormDrawer({
  open,
  onClose,
  onRaised,
  devices,
  defaultCustomerDeviceId,
  onUpload,
}: WarrantyClaimFormDrawerProps) {
  const raise = useRaiseWarrantyClaim();

  // `mode: 'onChange'` is what makes `isValid` track every keystroke, which is what holds Submit
  // disabled until the claim is actually complete (113319). Without it `isValid` only settles after
  // a submit attempt — and the AC forbids letting the Grower submit and then be told off.
  const form = useForm<WarrantyClaimFormValues>({
    resolver: zodResolver(warrantyClaimFormSchema),
    mode: 'onChange',
    defaultValues: {
      // Empty account-wide, so `customerDeviceId: z.string().min(1)` holds Submit disabled until
      // the Grower picks one — no extra gate, the schema already says exactly one Device per claim.
      customerDeviceId: defaultCustomerDeviceId ?? '',
      reason: undefined,
      description: '',
      images: [],
    },
  });

  const submit = (values: WarrantyClaimFormValues) =>
    raise
      .mutateAsync(
        {
          customerDeviceId: values.customerDeviceId,
          reason: values.reason,
          description: values.description,
          // The presign hands back `assetId` as the file's id; the claim references the uploads by it.
          imageAssetIds: values.images.map((image) => image.id),
        },
        { onSuccess: onRaised },
      )
      // The failure is already announced by the app-wide `<MutationToaster>` from the mutation's
      // `meta`, and 113319 wants the panel left standing with every value intact. Swallowing the
      // rejection here is what keeps `handleSubmit` from re-throwing it out of the submit event
      // handler, where nothing can await it.
      .catch(() => undefined);

  return (
    <FormDrawer
      open={open}
      // 113319: "the form closes immediately without a confirmation popup". `<FormDrawer>`'s dirty
      // guard only ever suppresses the *scrim* click, never these two controls, so x and Cancel are
      // already unconditional.
      onClose={onClose}
      title={WARRANTY_COPY.FORM_TITLE}
      form={form}
      onSubmit={submit}
      submitText={WARRANTY_COPY.SUBMIT}
      primaryDisabled={!form.formState.isValid}
    >
      <div className={styles.fields}>
        <SelectField
          name="customerDeviceId"
          control={form.control}
          label={WARRANTY_COPY.DEVICE_LABEL}
          placeholder={WARRANTY_COPY.SELECT_PLACEHOLDER}
          required
          // Locked iff the Grower arrived from a Device. Account-wide the field is a plain
          // single-select — Figma `430:403693` draws no disabled treatment there, and the
          // `.lockedDevice` ink override exists only to restore the Device flow's full-strength
          // Text/Primary through antd's disabled styling.
          disabled={Boolean(defaultCustomerDeviceId)}
          className={defaultCustomerDeviceId ? styles.lockedDevice : undefined}
          options={devices}
        />

        <SelectField
          name="reason"
          control={form.control}
          label={WARRANTY_COPY.REASON_LABEL}
          placeholder={WARRANTY_COPY.SELECT_PLACEHOLDER}
          required
          options={REASON_OPTIONS}
        />

        <TextAreaField
          name="description"
          control={form.control}
          label={WARRANTY_COPY.DESCRIPTION_LABEL}
          placeholder={WARRANTY_COPY.DESCRIPTION_PLACEHOLDER}
          required
          // `maxLength` is what makes typing stop at the limit with no error, which the AC calls
          // "graceful"; `showCount` renders Figma 944:564548's "0/2000" (antd's own default would
          // read "0 / 2000").
          maxLength={WARRANTY_DESCRIPTION_MAX}
          showCount={{
            formatter: ({ count, maxLength }) => `${count}/${maxLength ?? ''}`,
          }}
        />

        <UploadField
          name="images"
          control={form.control}
          label={WARRANTY_COPY.IMAGES_LABEL}
          required
          // Copy lives on the preset (ticket 01) — the instruction and all four validation messages
          // arrive with it, so nothing here restates them.
          {...UPLOAD_PRESETS.componentImages}
          onUpload={onUpload}
        />
      </div>
    </FormDrawer>
  );
}
